import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { adminPlaylistsApi, adminVideosApi, youtubeApi, authApi, adminTestimonialsApi, LINK_TYPES_TESTIMONIALS } from "../api/client";
import { getImageUrl } from "../utils/imageUtils";
import "./Admin.css";


// -- Composants utilitaires ---------------------------------------------------

function Notification({ type, texte }) {
  return (
    <div className={`admin-toast admin-toast--${type}`} role="status">
      <i className={`bi ${type === "ok" ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`}></i>
      <span>{texte}</span>
    </div>
  );
}

function Spinner() {
  return <span className="admin-spinner" aria-label="chargement..." />;
}

function Badge({ ok, texte }) {
  return (
    <span className={`admin-badge ${ok ? "admin-badge--ok" : "admin-badge--err"}`}>
      {texte}
    </span>
  );
}


// -- Page principale d'administration -----------------------------------------

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [playlists, setPlaylists] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [synchronisation, setSynchronisation] = useState(false);
  const [notification, setNotification] = useState(null);

  const [testimonials, setTestimonials] = useState([]);
  const [chargementTestimonials, setChargementTestimonials] = useState(true);

  useEffect(() => {
    chargerPlaylists();
    chargerTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function afficherNotification(type, texte) {
    setNotification({ type, texte });
    setTimeout(() => setNotification(null), 4000);
  }

  async function chargerPlaylists() {
    setChargement(true);
    try {
      const donnees = await adminPlaylistsApi.lister();
      setPlaylists(donnees);
    } catch (err) {
      afficherNotification("err", `Erreur de chargement : ${err.message}`);
    } finally {
      setChargement(false);
    }
  }

  async function chargerTestimonials() {
    setChargementTestimonials(true);
    try {
      const donnees = await adminTestimonialsApi.lister();
      setTestimonials(donnees);
    } catch (err) {
      afficherNotification("err", `Erreur de chargement des avis : ${err.message}`);
    } finally {
      setChargementTestimonials(false);
    }
  }

  async function synchroniserYoutube() {
    setSynchronisation(true);
    try {
      const resultat = await youtubeApi.synchroniser();
      afficherNotification(
        "ok",
        `synchronisation ytb terminee : ${resultat.message}`
      );
      chargerPlaylists();
    } catch (err) {
      afficherNotification("err", `erreur de synchronisation ytb : ${err.message}`);
    } finally {
      setSynchronisation(false);
    }
  }

  function connecterCompteYoutube() {
    // Redirection vers le backend pour démarrer le flux OAuth.
    // Avec les HttpOnly Cookies, le navigateur attache tout seul le cookie JWT lors de la requete.
    // Plus de query string !
    const urlApi = process.env.REACT_APP_API_URL ?? "";
    window.location.href = `${urlApi}/api/youtube/auth`;
  }

  return (
    <div className="admin-page">

      {/* Barre du haut */}
      <header className="admin-topbar">
        <div className="admin-topbar-brand">
          <span className="admin-topbar-title">Administration</span>
          <span className="admin-topbar-subtitle">Zzaelde</span>
        </div>
        <div className="admin-topbar-actions">
          <span className="admin-topbar-user"><i className="bi bi-person-circle"></i> {user?.username}</span>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => navigate("/")}>
            <i className="bi bi-box-arrow-up-right"></i> Voir le site
          </button>
          <button className="admin-btn admin-btn--danger-ghost admin-btn--sm" onClick={logout}>
            <i className="bi bi-box-arrow-right"></i> Déconnexion
          </button>
        </div>
      </header>

      {/* Message de confirmation ou d'erreur */}
      {notification && <Notification type={notification.type} texte={notification.texte} />}

      <div className="admin-body">

        {/* Navigation entre sections */}
        <nav className="admin-nav">
          <a className="admin-nav-link" href="#section-mot-de-passe"><i className="bi bi-key-fill"></i> Mot de passe</a>
          <a className="admin-nav-link" href="#section-youtube"><i className="bi bi-youtube"></i> YouTube</a>
          <a className="admin-nav-link" href="#section-playlists"><i className="bi bi-collection-play-fill"></i> Playlists & vidéos</a>
          <a className="admin-nav-link" href="#section-avis"><i className="bi bi-chat-quote-fill"></i> Avis clients</a>
        </nav>

        <main className="admin-main">

          {/* Section : changer le mot de passe */}
          <section className="admin-section" id="section-mot-de-passe">
            <h2 className="admin-section-title"><i className="bi bi-key-fill"></i> Mot de passe</h2>
            <FormMotDePasse notifier={afficherNotification} />
          </section>

          {/* Section : synchronisation YouTube */}
          <section className="admin-section" id="section-youtube">
            <h2 className="admin-section-title"><i className="bi bi-youtube"></i> YouTube</h2>
            <div className="admin-youtube-bar">
              <p className="admin-youtube-info">
                <i className="bi bi-info-circle"></i> Clique pour synchroniser playlist et video
              </p>
              <div className="admin-youtube-actions">
                <button
                  className="admin-btn admin-btn--ghost"
                  onClick={connecterCompteYoutube}
                >
                  <i className="bi bi-link-45deg"></i> Associer mon compte YouTube
                </button>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={synchroniserYoutube}
                  disabled={synchronisation}
                >
                  {synchronisation ? <><Spinner /> synchro...</> : <><i className="bi bi-arrow-repeat"></i> synchroniser depuis ytb</>}
                </button>
              </div>
            </div>
          </section>

          {/* Section : gestion des playlists et vidéos */}
          <section className="admin-section" id="section-playlists">
            <h2 className="admin-section-title"><i className="bi bi-collection-play-fill"></i> Playlists & vidéos</h2>
            {chargement ? (
              <div className="admin-loading"><Spinner /> chargement...</div>
            ) : playlists.length === 0 ? (
              <p className="admin-empty">
                aucune playlist.
              </p>
            ) : (
              <div className="admin-playlists">
                {playlists.map((playlist) => (
                  <CartePlaylist
                    key={playlist.id}
                    playlist={playlist}
                    onMiseAJour={chargerPlaylists}
                    notifier={afficherNotification}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Section : gestion des testimonials/avis */}
          <section className="admin-section" id="section-avis">
            <h2 className="admin-section-title"><i className="bi bi-chat-quote-fill"></i> Avis clients</h2>
            <FormNouveauTestimonial notifier={afficherNotification} onCree={chargerTestimonials} />
            {chargementTestimonials ? (
              <div className="admin-loading"><Spinner /> chargement...</div>
            ) : testimonials.length === 0 ? (
              <p className="admin-empty">aucun avis pour le moment.</p>
            ) : (
              <div className="admin-playlists">
                {testimonials.map((avis) => (
                  <CarteTestimonial
                    key={avis.id}
                    avis={avis}
                    onMiseAJour={chargerTestimonials}
                    notifier={afficherNotification}
                  />
                ))}
              </div>
            )}
          </section>

        </main>
      </div>
    </div>
  );
}


// -- Formulaire changement de mot de passe ------------------------------------

function FormMotDePasse({ notifier }) {
  const [ancien, setAncien] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e) {
    e.preventDefault();
    setEnvoi(true);
    try {
      await authApi.changerMotDePasse(ancien, nouveau);
      notifier("ok", "Mot de passe mis à jour");
      setAncien("");
      setNouveau("");
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="admin-password-form" onSubmit={soumettre}>
      <input
        className="admin-input"
        type="password"
        placeholder="Ancien mot de passe"
        value={ancien}
        onChange={(e) => setAncien(e.target.value)}
        required
      />
      <input
        className="admin-input"
        type="password"
        placeholder="Nouveau mot de passe"
        value={nouveau}
        onChange={(e) => setNouveau(e.target.value)}
        required
      />
      <button className="admin-btn admin-btn--primary" type="submit" disabled={envoi}>
        {envoi ? "mise a jour..." : <><i className="bi bi-key-fill"></i> Changer le mot de passe</>}
      </button>
    </form>
  );
}


// -- Carte d'une playlist -----------------------------------------------------

function CartePlaylist({ playlist, onMiseAJour, notifier }) {
  const [videosAffichees, setVideosAffichees] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [titre, setTitre] = useState(playlist.title);
  const [description, setDescription] = useState(playlist.description);
  const [sauvegarde, setSauvegarde] = useState(false);
  const [videos, setVideos] = useState(null);
  const [chargementVideos, setChargementVideos] = useState(false);
  const champFichier = useRef(null);

  async function sauvegarderPlaylist() {
    setSauvegarde(true);
    try {
      await adminPlaylistsApi.modifier(playlist.id, { title: titre, description });
      notifier("ok", "playlist mis a jour");
      setEnEdition(false);
      onMiseAJour();
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setSauvegarde(false);
    }
  }

  async function changerImage(e) {
    const fichier = e.target.files?.[0];
    if (!fichier) return;
    try {
      await adminPlaylistsApi.changerImage(playlist.id, fichier);
      notifier("ok", "image mis a jour");
      onMiseAJour();
    } catch (err) {
      notifier("err", err.message);
    }
    e.target.value = "";
  }

  async function supprimerPlaylist() {
  if (!window.confirm(`Supprimer définitivement la playlist "${playlist.title}" et toutes ses vidéos ?`)) return;
  try {
    await adminPlaylistsApi.supprimer(playlist.id);
    notifier("ok", "playlist supprimée");
    onMiseAJour();
  } catch (err) {
    notifier("err", err.message);
  }
}

  async function afficherOuMasquerVideos() {
    if (videosAffichees) {
      setVideosAffichees(false);
      return;
    }
    setVideosAffichees(true);
    if (videos !== null) return;
    setChargementVideos(true);
    try {
      const donnees = await adminVideosApi.listerPourPlaylist(playlist.id);
      setVideos(donnees);
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setChargementVideos(false);
    }
  }

  function mettreAJourVideo(videoModifiee) {
    setVideos((prev) => prev.map((v) => (v.id === videoModifiee.id ? videoModifiee : v)));
  }

  return (
    <div className="admin-playlist-card">

      {/* En-tête de la playlist */}
      <div className="admin-playlist-header">
        <img className="admin-playlist-thumb" src={getImageUrl(playlist.miniature)} alt={playlist.title} />

        <div className="admin-playlist-meta">
          {enEdition ? (
            <>
              <input
                className="admin-input"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="Titre"
              />
              <textarea
                className="admin-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description"
                rows={2}
              />
              <div className="admin-playlist-meta-actions">
                <button
                  className="admin-btn admin-btn--primary admin-btn--sm"
                  onClick={sauvegarderPlaylist}
                  disabled={sauvegarde}
                >
                  {sauvegarde ? "Enregistrement..." : <><i className="bi bi-check-lg"></i> Enregistrer</>}
                </button>
                <button
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={() => {
                    setTitre(playlist.title);
                    setDescription(playlist.description);
                    setEnEdition(false);
                  }}
                >
                  <i className="bi bi-x-lg"></i> annuler
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="admin-playlist-title">{playlist.title}</span>
              <span className="admin-playlist-desc">
                {playlist.description || <em>Pas de description</em>}
              </span>
            </>
          )}
        </div>

        {/* Actions sur la playlist */}
        <div className="admin-playlist-actions">
          {!enEdition && (
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => setEnEdition(true)}
            >
              <i className="bi bi-pencil-square"></i> modifier
            </button>
          )}
          <input
            ref={champFichier}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={changerImage}
          />
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => champFichier.current?.click()}
          >
            <i className="bi bi-image"></i> changer l'image
          </button>
          <button
            className="admin-btn admin-btn--danger admin-btn--sm"
            onClick={supprimerPlaylist}
          >
            <i className="bi bi-trash"></i> supprimer la playlist
          </button>
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={afficherOuMasquerVideos}
          >
            <i className={`bi ${videosAffichees ? "bi-chevron-up" : "bi-chevron-down"}`}></i>{" "}
            {videosAffichees
              ? "Masquer les vidéos"
              : `Vidéos (${playlist.videos?.length ?? "?"})`}
          </button>
        </div>
      </div>

      {/* Liste des vidéos (dépliable) */}
      {videosAffichees && (
        <div className="admin-videos-list">
          {chargementVideos ? (
            <div className="admin-loading"><Spinner /> chargement...</div>
          ) : videos?.length === 0 ? (
            <p className="admin-empty">pas de video dans la playlist</p>
          ) : (
            videos?.map((video) => (
              <LigneVideo
                key={video.id}
                video={video}
                onMiseAJour={mettreAJourVideo}
                notifier={notifier}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}


// -- Ligne d'une vidéo --------------------------------------------------------

function LigneVideo({ video, onMiseAJour, onSuppression, notifier }) {
  const [enEdition, setEnEdition] = useState(false);
  const [titre, setTitre] = useState(video.title);
  const [sauvegarde, setSauvegarde] = useState(false);

  async function sauvegarderVideo() {
    setSauvegarde(true);
    try {
      const videoModifiee = await adminVideosApi.modifier(video.id, { title: titre });
      notifier("ok", "titre modifie");
      setEnEdition(false);
      onMiseAJour(videoModifiee);
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setSauvegarde(false);
    }
  }

  async function basculerVisibilite() {
    try {
      if (video.masquee) {
        const videoModifiee = await adminVideosApi.restaurer(video.id);
        onMiseAJour({ ...videoModifiee, masquee: false });
        notifier("ok", "video visible sur le site");
      } else {
        await adminVideosApi.masquer(video.id);
        onMiseAJour({ ...video, masquee: true });
        notifier("ok", "video masquee du site");
      }
    } catch (err) {
      notifier("err", err.message);
    }
  }

  return (
    <div className={`admin-video-row ${video.masquee ? "admin-video-row--hidden" : ""}`}>
      <img className="admin-video-thumb" src={getImageUrl(video.miniature)} alt={video.title} loading="lazy" />

      <div className="admin-video-meta">
        {enEdition ? (
          <input
            className="admin-input"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
          />
        ) : (
          <span className="admin-video-title">{video.title}</span>
        )}
        <span className="admin-video-date">{video.publishedAt}</span>
        {video.masquee && <Badge ok={false} texte="masquee" />}
      </div>

      <div className="admin-video-actions">
        {enEdition ? (
          <>
            <button
              className="admin-btn admin-btn--primary admin-btn--sm"
              onClick={sauvegarderVideo}
              disabled={sauvegarde}
            >
              {sauvegarde ? "..." : <i className="bi bi-check-lg"></i>}
            </button>
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => { setTitre(video.title); setEnEdition(false); }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </>
        ) : (
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => setEnEdition(true)}
          >
            <i className="bi bi-pencil"></i> renommer
          </button>
        )}
        <button
          className={`admin-btn admin-btn--sm ${video.masquee ? "admin-btn--primary" : "admin-btn--danger-ghost"}`}
          onClick={basculerVisibilite}
        >
          <i className={`bi ${video.masquee ? "bi-eye" : "bi-eye-slash"}`}></i> {video.masquee ? "rendre visible" : "masquer"}
        </button>
      </div>
    </div>
  );
}


// -- Formulaire de création d'un avis -----------------------------------------

function FormNouveauTestimonial({ notifier, onCree }) {
  const [nom, setNom] = useState("");
  const [texte, setTexte] = useState("");
  const [lien, setLien] = useState("");
  const [typeLien, setTypeLien] = useState(LINK_TYPES_TESTIMONIALS[0]);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e) {
    e.preventDefault();
    setEnvoi(true);
    try {
      await adminTestimonialsApi.creer({ name: nom, text: texte, link: lien, link_type: typeLien });
      notifier("ok", "avis cree");
      setNom("");
      setTexte("");
      setLien("");
      setTypeLien(LINK_TYPES_TESTIMONIALS[0]);
      onCree();
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="admin-testimonial-form" onSubmit={soumettre}>
      <input
        className="admin-input"
        placeholder="nom du client"
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        maxLength={256}
        required
      />
      <textarea
        className="admin-textarea"
        placeholder="commentaire/métier"
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        maxLength={256}
        rows={2}
        required
      />
      <input
        className="admin-input"
        placeholder="https://..."
        type="url"
        value={lien}
        onChange={(e) => setLien(e.target.value)}
        maxLength={256}
        required
      />
      <select
        className="admin-input"
        value={typeLien}
        onChange={(e) => setTypeLien(e.target.value)}
      >
        {LINK_TYPES_TESTIMONIALS.map((type) => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>
      <button className="admin-btn admin-btn--primary admin-btn--sm" type="submit" disabled={envoi}>
        {envoi ? "creation..." : <><i className="bi bi-plus-lg"></i> ajouter un avis</>}
      </button>
    </form>
  );
}


// -- Carte d'un avis -----------------------------------------------------------

function CarteTestimonial({ avis, onMiseAJour, notifier }) {
  const [enEdition, setEnEdition] = useState(false);
  const [nom, setNom] = useState(avis.name);
  const [texte, setTexte] = useState(avis.text);
  const [lien, setLien] = useState(avis.link);
  const [typeLien, setTypeLien] = useState(avis.link_type);
  const [sauvegarde, setSauvegarde] = useState(false);
  const champFichier = useRef(null);

  async function sauvegarderTestimonial() {
    setSauvegarde(true);
    try {
      await adminTestimonialsApi.modifier(avis.id, { name: nom, text: texte, link: lien, link_type: typeLien });
      notifier("ok", "avis mis a jour");
      setEnEdition(false);
      onMiseAJour();
    } catch (err) {
      notifier("err", err.message);
    } finally {
      setSauvegarde(false);
    }
  }

  async function changerImage(e) {
    const fichier = e.target.files?.[0];
    if (!fichier) return;
    try {
      await adminTestimonialsApi.changerImage(avis.id, fichier);
      notifier("ok", "image mise a jour");
      onMiseAJour();
    } catch (err) {
      notifier("err", err.message);
    }
    e.target.value = "";
  }

  async function supprimer() {
    if (!window.confirm(`Supprimer definitivement l'avis de '${avis.name}' ?`)) return;
    try {
      await adminTestimonialsApi.supprimer(avis.id);
      notifier("ok", "avis supprime");
      onMiseAJour();
    } catch (err) {
      notifier("err", err.message);
    }
  }

  return (
    <div className="admin-playlist-card">
      <div className="admin-playlist-header">
        <img className="admin-playlist-thumb" src={getImageUrl(avis.image)} alt={avis.name} />

        <div className="admin-playlist-meta">
          {enEdition ? (
            <>
              <input
                className="admin-input"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="nom"
                maxLength={256}
              />
              <textarea
                className="admin-textarea"
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                placeholder="commentaire"
                maxLength={256}
                rows={2}
              />
              <input
                className="admin-input"
                value={lien}
                onChange={(e) => setLien(e.target.value)}
                placeholder="https://..."
                type="url"
                maxLength={256}
              />
              <select
                className="admin-input"
                value={typeLien}
                onChange={(e) => setTypeLien(e.target.value)}
              >
                {LINK_TYPES_TESTIMONIALS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <div className="admin-playlist-meta-actions">
                <button
                  className="admin-btn admin-btn--primary admin-btn--sm"
                  onClick={sauvegarderTestimonial}
                  disabled={sauvegarde}
                >
                  {sauvegarde ? "enregistrement..." : <><i className="bi bi-check-lg"></i> enregistrer</>}
                </button>
                <button
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={() => {
                    setNom(avis.name);
                    setTexte(avis.text);
                    setLien(avis.link);
                    setTypeLien(avis.link_type);
                    setEnEdition(false);
                  }}
                >
                  <i className="bi bi-x-lg"></i> annuler
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="admin-playlist-title">{avis.name}</span>
              <span className="admin-playlist-desc">{avis.text}</span>
              <span className="admin-playlist-desc">{avis.link} ({avis.link_type})</span>
            </>
          )}
        </div>

        <div className="admin-playlist-actions">
          {!enEdition && (
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => setEnEdition(true)}
            >
              <i className="bi bi-pencil-square"></i> modifier
            </button>
          )}
          <input
            ref={champFichier}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: "none" }}
            onChange={changerImage}
          />
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => champFichier.current?.click()}
          >
            <i className="bi bi-image"></i> changer l'image
          </button>
          <button
            className="admin-btn admin-btn--danger-ghost admin-btn--sm"
            onClick={supprimer}
          >
            <i className="bi bi-trash"></i> supprimer
          </button>
        </div>
      </div>
    </div>
  );
}
